import React, { forwardRef, useEffect, useRef } from 'react';
import {
  RotateCcw,
  Bell,
  BellOff,
  Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../../hooks/useNotifications';
import { NotificationItem } from './NotificationItem';
import { formatEasternTime } from '@/utils/dateFormatter';

interface NotificationDropdownProps {
  /** Array of notifications to display */
  notifications: Notification[];
  /** Count of unread notifications within the loaded window (drives Mark-all visibility) */
  unreadCount: number;
  /** Server-side TRUE unacknowledged total (matches the bell badge) */
  totalUnacknowledged?: number;
  /** Whether notifications are loading */
  isLoading: boolean;
  /** Last fetch timestamp */
  lastFetch: Date | null;
  /** Callback when dropdown should close */
  onClose: () => void;
  /** Take ownership of one alert — the deliberate, true acknowledgment (button, not click) */
  onAcknowledge: (id: string) => void;
  /** Callback when notification is removed */
  onRemoveNotification: (id: string) => void;
  /** Callback to refresh notifications */
  onRefresh: () => void;
  /** Maximum height of the dropdown */
  maxHeight?: string;
  /** Custom CSS classes */
  className?: string;
}

/**
 * NotificationDropdown component for displaying notifications list
 * 
 * Features:
 * - Scrollable list of notifications
 * - Mark all as read button
 * - Refresh button
 * - Empty state
 * - Loading state
 * - Last updated timestamp
 * - Keyboard navigation
 * - Focus trapping
 */
export const NotificationDropdown = forwardRef<HTMLDivElement, NotificationDropdownProps>(
  ({
    notifications,
    unreadCount,
    totalUnacknowledged,
    isLoading,
    lastFetch,
    onClose,
    onAcknowledge,
    onRemoveNotification,
    onRefresh,
    maxHeight = '400px',
    className = '',
  }, ref) => {
    const navigate = useNavigate();
    // Focus management refs
    const firstFocusableRef = useRef<HTMLElement | null>(null);
    const lastFocusableRef = useRef<HTMLElement | null>(null);

    /**
     * Handle keyboard navigation for dropdown with focus trapping
     */
    useEffect(() => {
      if (!ref || typeof ref === 'function' || !ref.current) return;

      const dropdown = ref.current;
      const focusableElements = dropdown.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      
      const focusableArray = Array.from(focusableElements) as HTMLElement[];
      firstFocusableRef.current = focusableArray[0];
      lastFocusableRef.current = focusableArray[focusableArray.length - 1];

      // Focus first element when dropdown opens
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 50);

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
          return;
        }

        if (event.key === 'Tab') {
          if (event.shiftKey) {
            // Shift + Tab: move backwards
            if (document.activeElement === firstFocusableRef.current) {
              event.preventDefault();
              lastFocusableRef.current?.focus();
            }
          } else {
            // Tab: move forwards  
            if (document.activeElement === lastFocusableRef.current) {
              event.preventDefault();
              firstFocusableRef.current?.focus();
            }
          }
        }
      };

      dropdown.addEventListener('keydown', handleKeyDown);
      return () => dropdown.removeEventListener('keydown', handleKeyDown);
    }, [onClose, ref]);

    /**
     * Format last fetch time
     */
    const formatLastFetch = (lastFetch: Date | null): string => {
      if (!lastFetch) return 'Never';
      
      const now = new Date();
      const diff = now.getTime() - lastFetch.getTime();
      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(diff / 60000);

      if (seconds < 10) return 'Just now';
      if (seconds < 60) return `${seconds}s ago`;
      if (minutes < 60) return `${minutes}m ago`;
      
      return formatEasternTime(lastFetch);
    };

    /**
     * Handle refresh
     */
    const handleRefresh = () => {
      onRefresh();
    };

    /**
     * Render empty state
     */
    const renderEmptyState = () => (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
        <BellOff className="w-12 h-12 text-[var(--color-text-secondary)] mb-3" />
        <h3 className="text-sm font-medium text-[var(--color-text-primary)] mb-1">No notifications</h3>
        <p className="text-xs text-[var(--color-text-secondary)] max-w-[200px]">
          You're all caught up! We'll notify you when something requires your attention.
        </p>
      </div>
    );

    /**
     * Render loading state
     */
    const renderLoadingState = () => (
      <div className="flex flex-col items-center justify-center py-8 px-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent)] mb-3" />
        <p className="text-sm text-[var(--color-text-secondary)]">Loading notifications...</p>
      </div>
    );

    return (
      <div
        ref={ref}
        className={`absolute top-full right-0 mt-2 w-96 bg-[var(--color-bg-card)] rounded-lg shadow-xl border border-[var(--color-border)] z-50 ${className}`}
        role="dialog"
        aria-label="Notifications"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-[var(--color-text-secondary)]" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Notifications</h2>
            {/* TRUE unacknowledged total (matches the bell badge), not the count within
                the loaded window — which used to read a different, smaller number. */}
            {(totalUnacknowledged ?? unreadCount) > 0 && (
              <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                {totalUnacknowledged ?? unreadCount}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-1.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)] rounded transition-colors disabled:opacity-50"
              aria-label="Refresh notifications"
              title="Refresh notifications"
            >
              <RotateCcw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div 
          className="overflow-y-auto"
          style={{ maxHeight }}
        >
          {isLoading && notifications.length === 0 ? (
            renderLoadingState()
          ) : notifications.length === 0 ? (
            renderEmptyState()
          ) : (
            <div className="divide-y divide-[var(--color-border)]">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onAcknowledge={onAcknowledge}
                  onRemove={onRemoveNotification}
                  onClick={(n) => {
                    // Spec 193: jump to the alert's source page, then close the dropdown.
                    // Navigation only — reviewing an alert never acknowledges it.
                    if (n.resourceUrl) navigate(n.resourceUrl);
                    onClose();
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[var(--color-border)] bg-[var(--color-bg-subtle)] rounded-b-lg">
          <div className="flex items-center justify-between text-xs text-[var(--color-text-secondary)]">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>Updated {formatLastFetch(lastFetch)}</span>
            </div>
            
            {notifications.length > 0 && (
              <span>
                Showing {notifications.length} most recent
                {typeof totalUnacknowledged === 'number' &&
                  totalUnacknowledged > 0 &&
                  ` · ${totalUnacknowledged} unacknowledged`}
              </span>
            )}
          </div>
        </div>

        {/* Loading overlay for refresh */}
        {isLoading && notifications.length > 0 && (
          <div className="absolute inset-0 bg-black/15 dark:bg-black/45 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-2 px-3 py-2 bg-[var(--color-bg-card)] rounded-md shadow-md">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--color-accent)]" />
              <span className="text-sm text-[var(--color-text-secondary)]">Refreshing...</span>
            </div>
          </div>
        )}
      </div>
    );
  }
);

NotificationDropdown.displayName = 'NotificationDropdown';

export default NotificationDropdown;